import { useCallback, useEffect, useRef, useState } from "react";
import { askConfirmation, showErrorToast, showResponseToast } from "../helpers/message";
import { isRequestCancelled } from "../apis/useAxios";

/**
 * Standard save / delete flow for master screens:
 * busy flag, API call, success/error toast (no blocking popup), then an optional callback.
 * The API call must resolve to the usual { isSuccess, message, payload } response.
 */
export default function useCrudActions() {
  const [busy, setBusy] = useState(false);
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  const run = useCallback(async (apiCall, { onSuccess } = {}) => {
    setBusy(true);
    try {
      const response = await apiCall();
      const ok = showResponseToast({
        isSuccess: Boolean(response?.isSuccess),
        message: response?.message ?? "",
      });
      if (ok && onSuccess) await onSuccess(response, ok);
      return response;
    } catch (err) {
      if (!isRequestCancelled(err)) showErrorToast(err?.message);
      return null;
    } finally {
      if (mounted.current) setBusy(false);
    }
  }, []);

  const save = useCallback((apiCall, options) => run(apiCall, options), [run]);

  const remove = useCallback(
    async (apiCall, { confirmMessage, ...options } = {}) => {
      const confirmed = await askConfirmation(
        confirmMessage ?? "Are you sure you want to delete this record?",
        "Yes, delete",
        "No",
      );
      return confirmed ? run(apiCall, options) : null;
    },
    [run],
  );

  return { busy, save, remove };
}
